import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { useVisibleSections, type VisibleSection } from "../../hooks/useVisibleSections";
import { type NavLinkItem, type NavGroup } from "../../nav/navConfig";
import { isProfileRoute, PROFILE_PATH } from "../../lib/profileTabs";
import { cn } from "../../utils/cn";
import { trackEvent } from "../../lib/auditCollector";
import { Badge } from "../ui/Badge";
import { Icon } from "../ui/Icon";
import { Tooltip } from "../ui/Tooltip";
import { Logo } from "./Logo";
import { SidebarCategoryReorder } from "./SidebarCategoryReorder";
import { SystemStatusDot } from "../system/SystemStatusDot";
import { AppStatusDot } from "../system/AppStatusDot";

type SidebarNavProps = {
  collapsed: boolean;
};

export const NAV_RAIL_WIDTH = 76;
export const NAV_FLYOUT_WIDTH = 268;
export const NAV_OPEN_WIDTH = NAV_RAIL_WIDTH + NAV_FLYOUT_WIDTH;

export const SidebarNav = ({ collapsed }: SidebarNavProps) => {
  const {
    visibleSections,
    fallbackSection,
    isAdmin,
    isNavItemActive,
    isNavItemTabActive,
    hasUpdatesAvailable,
    installedSoftwareIds,
    dynamicSoftwareGroups,
    quickNavItems,
    userPrefs,
    updatePrefs,
    prefsSaving
  } = useVisibleSections();
  const location = useLocation();
  const prefersReducedMotion = usePrefersReducedMotion();

  const [reorderMode, setReorderMode] = useState(false);

  const railRef = useRef<HTMLDivElement | null>(null);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const [hoveredSectionId, setHoveredSectionId] = useState<string | null>(null);
  const [pinnedSectionId, setPinnedSectionId] = useState<string | null>(null);
  const [openGroupKeys, setOpenGroupKeys] = useState<Set<string>>(new Set());
  const previousSectionIdRef = useRef<string | null>(null);
  const flyoutHideTimerRef = useRef<number | null>(null);
  const [flyoutVisible, setFlyoutVisible] = useState(false);

  const makeGroupKey = (sectionId: string, groupId: string) => `${sectionId}::${groupId}`;

  const navVars = {
    "--nav-rail-width": `${NAV_RAIL_WIDTH}px`,
    "--nav-flyout-width": `${NAV_FLYOUT_WIDTH}px`,
    "--nav-open-width": `${NAV_OPEN_WIDTH}px`
  } as CSSProperties;

  const activeSectionId = useMemo(() => {
    for (const section of visibleSections) {
      for (const item of section.items) {
        if (isNavItemActive(item, location.pathname, location.search)) {
          return section.id;
        }
      }
      // Also check navigable group headers (groups with a `path` property), recursively
      const checkGroupPaths = (groups: NavGroup[]): boolean =>
        groups.some((group) => {
          if (group.path && (location.pathname === group.path || location.pathname.startsWith(`${group.path}/`))) return true;
          return group.groups ? checkGroupPaths(group.groups) : false;
        });
      if (section.groups && checkGroupPaths(section.groups)) {
        return section.id;
      }
    }
    if (isProfileRoute(location.pathname)) {
      return fallbackSection.id;
    }
    return undefined;
  }, [fallbackSection.id, location.pathname, location.search, location.hash, visibleSections]);

  const activeItemId = useMemo(() => {
    for (const section of visibleSections) {
      for (const item of section.items) {
        if (isNavItemActive(item, location.pathname, location.search)) return item.id;
      }
    }
    for (const item of fallbackSection.items) {
      if (isNavItemActive(item, location.pathname, location.search)) return item.id;
    }
    return undefined;
  }, [fallbackSection.items, location.pathname, location.search, location.hash, visibleSections]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      if (flyoutHideTimerRef.current) {
        window.clearTimeout(flyoutHideTimerRef.current);
        flyoutHideTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (collapsed) {
      setHoveredSectionId(null);
      setPinnedSectionId(null);
    }
  }, [collapsed]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    if (pinnedSectionId) return;
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setHoveredSectionId(null);
    }, 140);
  };

  const baseSectionId = !collapsed ? activeSectionId ?? null : null;
  const resolvedSectionId = pinnedSectionId ?? hoveredSectionId ?? baseSectionId;
  const resolvedSection = visibleSections.find((section) => section.id === resolvedSectionId) ?? null;
  const fallbackActiveSection = useMemo(() => {
    if (collapsed || resolvedSection) return null;
    if (!activeSectionId) return null;
    if (activeSectionId === fallbackSection.id) return fallbackSection;
    return null;
  }, [collapsed, resolvedSection, activeSectionId, fallbackSection]);
  const currentSection = resolvedSection ?? fallbackActiveSection;
  const flyoutOpen = Boolean(currentSection);

  // Exit reorder mode when navigating away from the software section
  useEffect(() => {
    if (currentSection?.id !== "software") {
      setReorderMode(false);
    }
  }, [currentSection?.id]);

  useEffect(() => {
    if (flyoutHideTimerRef.current) {
      window.clearTimeout(flyoutHideTimerRef.current);
      flyoutHideTimerRef.current = null;
    }

    if (flyoutOpen) {
      setFlyoutVisible(true);
      return;
    }

    const delay = prefersReducedMotion ? 0 : 200;
    flyoutHideTimerRef.current = window.setTimeout(() => {
      setFlyoutVisible(false);
      flyoutHideTimerRef.current = null;
    }, delay);
  }, [flyoutOpen, prefersReducedMotion]);

  useEffect(() => {
    if (!currentSection || !currentSection.groups) {
      setOpenGroupKeys(new Set());
      previousSectionIdRef.current = currentSection?.id ?? null;
      return;
    }

    const isAccountSection = currentSection.id === fallbackSection.id;
    const groupMatcher = isAccountSection ? isNavItemTabActive : isNavItemActive;

    // Recursively collect keys for all groups that should be auto-expanded
    const collectActiveKeys = (groups: NavGroup[]): string[] =>
      groups.flatMap((group) => {
        const keys: string[] = [];
        const hasDirectActiveChild = group.items.some((item) => groupMatcher(item, location.pathname, location.search));
        const isGroupHeaderActive = group.path && (location.pathname === group.path || location.pathname.startsWith(`${group.path}/`));
        // Recursively check nested groups
        const nestedKeys = group.groups ? collectActiveKeys(group.groups) : [];
        const hasActiveNested = nestedKeys.length > 0;

        if (hasDirectActiveChild || isGroupHeaderActive || hasActiveNested) {
          keys.push(makeGroupKey(currentSection!.id, group.id));
        }
        keys.push(...nestedKeys);
        return keys;
      });

    const activeKeys = collectActiveKeys(currentSection.groups);

    setOpenGroupKeys((prev) => {
      const base = currentSection.id === previousSectionIdRef.current ? new Set(prev) : new Set<string>();
      activeKeys.forEach((key) => base.add(key));
      return base;
    });

    previousSectionIdRef.current = currentSection.id;
  }, [currentSection, fallbackSection.id, location.pathname, location.search, location.hash]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent | MouseEvent) => {
      if (pinnedSectionId) return;
      const target = event.target as Node;
      if (railRef.current?.contains(target)) return;
      if (flyoutRef.current?.contains(target)) return;
      setHoveredSectionId(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [pinnedSectionId]);

  const handleSectionIntent = (sectionId: string) => {
    clearCloseTimer();
    setHoveredSectionId(sectionId);
  };

  const handlePinToggle = () => {
    if (!currentSection) return;
    setPinnedSectionId((prev) => (prev === currentSection.id ? null : currentSection.id));
    setHoveredSectionId((prev) => prev ?? currentSection.id);
  };

  const handleLinkSelect = (itemId?: string) => {
    trackEvent({ category: "ui", action: "sidebar.link-click", component: "SidebarNav", details: itemId ? { itemId } : undefined });
    if (!pinnedSectionId) {
      setHoveredSectionId(null);
    }
  };

  const toggleGroup = (groupId: string) => {
    if (!currentSection) return;
    const key = makeGroupKey(currentSection.id, groupId);
    setOpenGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const currentCategoryIds = useMemo(
    () => dynamicSoftwareGroups?.map((g) => g.id) ?? [],
    [dynamicSoftwareGroups]
  );

  const focusFirstFlyoutLink = () => {
    if (!flyoutRef.current) return;
    const focusable = flyoutRef.current.querySelector<HTMLElement>(
      "a, button, [tabindex]:not([tabindex='-1'])"
    );
    focusable?.focus();
  };

  const renderFlyoutLink = (
    item: NavLinkItem,
    options?: { nested?: boolean; showIcon?: boolean; timeline?: boolean }
  ) => {
    const isTimeline = options?.timeline === true;
    // Timeline active detection varies by item type:
    // - Profile items (notification events): use activeItemId (tab-aware)
    // - Hash-link items (settings sections, streaming flyouts): match location.hash
    // - Page-link items (CC sub-pages): match location.pathname exactly
    // We intentionally skip the generic activeItemId fallback because
    // section.items is flattened — activeItemId always matches the first
    // hash-link item on the page, making it permanently "active".
    const isTimelineActive = isTimeline && (() => {
      if (item.path.startsWith(PROFILE_PATH) && activeItemId === item.id) return true;
      const hashIdx = item.path.indexOf("#");
      if (hashIdx !== -1) {
        if (!location.hash) return false;
        return location.pathname === item.path.slice(0, hashIdx)
          && location.hash === item.path.slice(hashIdx);
      }
      // Page-link items: exact pathname match (not startsWith, to avoid
      // /control-center matching /control-center/server-users).
      // matchSubRoutes opts into prefix matching for items with deep sub-routes
      // (e.g., Media Portal → /control-center/media/item/:serverType/:itemId).
      if (item.matchSubRoutes) {
        return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
      }
      return location.pathname === item.path;
    })();
    const baseClasses = isTimeline
      ? "group/tl relative flex items-center gap-2 rounded-md py-2 pl-3 pr-3 text-xs transition-colors duration-150"
      : cn(
          "flex items-center justify-between gap-2 rounded-md py-2 text-sm transition-colors duration-150",
          options?.nested ? "pl-9 pr-3" : "px-3"
        );

    const showIcon = !isTimeline && options?.showIcon !== false && Boolean(item.icon);

    const content = isTimeline ? (
      <span className="flex min-w-0 items-center gap-2">
        <span className="absolute -left-[3px] top-1/2 -translate-y-1/2">
          {isTimelineActive && (
            <span className="absolute inset-[-3px] animate-ping rounded-full bg-primary/30" />
          )}
          <span
            className={cn(
              "block h-1.5 w-1.5 rounded-full transition-colors duration-150",
              isTimelineActive
                ? "bg-primary shadow-[0_0_6px_1px] shadow-primary/40"
                : "bg-muted-foreground/40 group-hover/tl:bg-primary/60"
            )}
          />
        </span>
        <span className="truncate text-xs">{item.label}</span>
      </span>
    ) : (
      <span className={cn("flex min-w-0 items-center gap-3", !showIcon && "pl-1") }>
        {showIcon && <Icon icon={item.icon ?? ""} className="h-4 w-4" />}
        <span className="truncate">{item.label}</span>
      </span>
    );

    if (item.disabled) {
      return (
        <div
          key={item.id}
          className={cn(
            baseClasses,
            "cursor-not-allowed text-muted-foreground/60"
          )}
          aria-disabled="true"
        >
          {content}
          <span className="rounded-sm bg-muted/70 px-1 py-[1px] text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
            Coming soon
          </span>
        </div>
      );
    }

    if (item.external) {
      const quickNavItem = quickNavItems.find((qi) => qi.id === item.id);
      const showExternalStatusDot =
        currentSection?.id === "software" &&
        installedSoftwareIds.has(item.id) &&
        quickNavItem?.showServiceStatus !== false;

      return (
        <a
          key={item.id}
          href={item.path}
          target="_blank"
          rel="noreferrer"
          className={cn(
            baseClasses,
            "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          )}
          onClick={() => handleLinkSelect(item.id)}
        >
          {content}
          <span className="flex items-center gap-1.5">
            {showExternalStatusDot && (
              <AppStatusDot appId={item.id} appLabel={item.label} appPath={item.path} />
            )}
            <Icon icon="lucide:arrow-up-right" className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </span>
          <span className="sr-only">(opens in new tab)</span>
        </a>
      );
    }

    const quickNavItemForStatus = quickNavItems.find((qi) => qi.id === item.id);
    const shouldShowStatusDot =
      currentSection?.id === "software" &&
      installedSoftwareIds.has(item.id) &&
      quickNavItemForStatus?.showServiceStatus !== false;

    // Show update badge on changelog item when updates are available (admin-only)
    const shouldShowUpdateBadge = item.id === "system-changelog" && isAdmin && hasUpdatesAvailable;

    // Hash-link items (e.g. /streaming/dashboard#analytics) open in-page
    // flyout panels, not new pages -- no trailing chevron needed.
    const isHashLink = item.path.includes("#");

    const trailingAccessory = shouldShowStatusDot ? (
      <AppStatusDot appId={item.id} appLabel={item.label} appPath={item.path} />
    ) : shouldShowUpdateBadge ? (
      <Badge variant="primary" size="xs" className="animate-pulse">
        Update
      </Badge>
    ) : isHashLink ? null : (
      <Icon icon="lucide:chevron-right" className="h-4 w-4 text-muted-foreground" />
    );

    return (
      <NavLink
        key={item.id}
        to={item.path}
        end={!item.matchSubRoutes}
        className={({ isActive: routerIsActive }) => {
          // Timeline items use hash-based active detection (isTimelineActive)
          // so only the matching section pulses, not all items on the same page.
          // Profile sub-items use activeItemId to avoid NavLink marking all
          // /profile items as active.
          const isProfileItem = item.path.startsWith(PROFILE_PATH);
          const isActive = isTimeline
            ? isTimelineActive
            : isProfileItem
              ? activeItemId === item.id
              : routerIsActive || activeItemId === item.id;
          const activeClass = isTimeline
            ? "text-foreground"
            : "bg-muted/80 text-foreground";
          const inactiveClass = isTimeline
            ? "text-muted-foreground hover:text-foreground"
            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground";
          return cn(baseClasses, isActive ? activeClass : inactiveClass);
        }}
        onClick={(e) => {
          if (isHashLink) {
            const hash = item.path.split("#")[1];
            if (hash) {
              // Streaming pages use a custom event to open flyout panels in place.
              if (location.pathname.startsWith("/streaming/dashboard")) {
                e.preventDefault();
                window.dispatchEvent(
                  new CustomEvent("streaming-flyout-open", { detail: hash })
                );
              } else {
                // For all other hash links (e.g. settings sub-sections), React
                // Router's navigate() doesn't trigger browser hash-scrolling when
                // the pathname hasn't changed.  Set the hash directly (updates
                // both React Router's location and the browser URL), then scroll.
                const itemPathname = item.path.split("#")[0];
                if (location.pathname === itemPathname) {
                  e.preventDefault();
                  window.location.hash = hash;
                  const el = document.getElementById(hash);
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }
              }
            }
          }
          handleLinkSelect(item.id);
        }}
      >
        {content}
        {isTimeline ? null : trailingAccessory}
      </NavLink>
    );
  };

  const renderFlyoutGroup = (group: NavGroup, depth = 0) => {
    if (!currentSection) return null;

    const key = makeGroupKey(currentSection.id, group.id);
    const isOpen = openGroupKeys.has(key);
    const isAccountGroup = currentSection.id === fallbackSection.id;
    const groupMatcher = isAccountGroup ? isNavItemTabActive : isNavItemActive;
    const hasActiveChild = group.items.some((item) =>
      groupMatcher(item, location.pathname, location.search)
    );
    // Also check nested child groups for active items
    const hasActiveNestedChild = group.groups?.some((childGroup) => {
      if (childGroup.path && (location.pathname === childGroup.path || location.pathname.startsWith(`${childGroup.path}/`))) return true;
      return childGroup.items.some((item) => groupMatcher(item, location.pathname, location.search));
    }) ?? false;
    // Check if group header itself is active (when group has a path)
    const isGroupHeaderActive = group.path
      ? location.pathname === group.path && !location.search
      : false;
    const panelId = `${group.id}-panel`;

    // Navigable group header (group has a path property)
    // Renders as a dual-action row: clicking the label navigates, clicking the chevron toggles.
    if (group.path) {
      return (
        <>
          <NavLink
            to={group.path}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md py-2 text-sm transition-colors duration-150",
              depth > 0 ? "pl-9 pr-3" : "px-3",
              isOpen || hasActiveChild || hasActiveNestedChild || isGroupHeaderActive
                ? "bg-muted/80 text-foreground"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            )}
            onClick={() => handleLinkSelect(group.id)}
          >
            <span className="flex min-w-0 items-center gap-3">
              <Icon icon={group.icon ?? "lucide:folder"} className="h-4 w-4" />
              <span className="truncate">{group.label}</span>
            </span>
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={panelId}
              className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleGroup(group.id);
              }}
            >
              <Icon
                icon="lucide:chevron-down"
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform",
                  isOpen ? "rotate-180" : "rotate-0"
                )}
                aria-hidden
              />
            </button>
          </NavLink>
          <div
            id={panelId}
            className={cn("mt-1 space-y-1", isOpen ? "block" : "hidden")}
          >
            {(() => {
              // All items under a navigable group are sub-navigation targets
              // (hash sections or child pages) — render as timeline dots.
              return (
                <>
                  {group.items.length > 0 && (
                    <div className="ml-[2.75rem] border-l border-border/40 py-0.5">
                      {group.items.map((item) => (
                        <div key={item.id}>
                          {renderFlyoutLink(item, { timeline: true })}
                        </div>
                      ))}
                    </div>
                  )}
                  {group.groups?.map((childGroup) => (
                    <div key={childGroup.id}>
                      {renderFlyoutGroup(childGroup, depth + 1)}
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        </>
      );
    }

    // Non-navigable group header (original behavior)
    return (
      <div className="rounded-md border border-border/60 bg-muted/50">
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          className={cn(
            "flex w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
            isOpen || hasActiveChild
              ? "bg-primary/5 text-foreground"
              : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          )}
          onClick={() => toggleGroup(group.id)}
        >
          <span className="flex items-center gap-2">
            <Icon icon={group.icon ?? "lucide:folder"} className="h-4 w-4" />
            <span className="truncate">{group.label}</span>
          </span>
          <Icon
            icon="lucide:chevron-down"
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              isOpen ? "rotate-180" : "rotate-0"
            )}
            aria-hidden
          />
        </button>
        <div
          id={panelId}
          className={cn("mt-1 space-y-1 pb-2", isOpen ? "block" : "hidden")}
        >
          {(() => {
            const showGroupIcons = currentSection.id !== "software";
            const regular: typeof group.items = [];
            const timeline: typeof group.items = [];
            for (const item of group.items) {
              // Notification event sub-items render as timeline dots.
              // These are dynamic items with IDs like "notification-settings-events-applications".
              // The parent "notification-settings-events" item stays as a regular nav item.
              const isTl =
                item.id.startsWith("notification-settings-events-") &&
                item.id !== "notification-settings-events";
              (isTl ? timeline : regular).push(item);
            }
            return (
              <>
                {regular.map((item) => (
                  <div key={item.id}>
                    {renderFlyoutLink(item, { nested: true, showIcon: showGroupIcons })}
                  </div>
                ))}
                {timeline.length > 0 && (
                  <div className="ml-[2.75rem] border-l border-border/40 py-0.5">
                    {timeline.map((item) => (
                      <div key={item.id}>
                        {renderFlyoutLink(item, { timeline: true })}
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    );
  };

  const renderRailButton = (section: VisibleSection) => {
    const isActiveSection = section.id === activeSectionId;
    const isOpen = flyoutOpen && resolvedSectionId === section.id;
    const showTooltip = collapsed || !isOpen;

    const button = (
      <button
        key={section.id}
        type="button"
        aria-label={section.label}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={cn(
          "relative flex h-11 w-full items-center justify-center rounded-md border border-transparent text-muted-foreground transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
          isOpen || isActiveSection
            ? "bg-muted/70 text-foreground border-border/60"
            : "hover:bg-muted/60 hover:text-foreground"
        )}
        onMouseEnter={() => handleSectionIntent(section.id)}
        onMouseLeave={scheduleClose}
        onFocus={() => handleSectionIntent(section.id)}
        onBlur={scheduleClose}
        onClick={() => handleSectionIntent(section.id)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " " || event.key === "ArrowRight") {
            event.preventDefault();
            handleSectionIntent(section.id);
            window.setTimeout(() => focusFirstFlyoutLink(), 0);
          }
        }}
      >
        <Icon icon={section.icon ?? "lucide:folder"} className="h-5 w-5" />
      </button>
    );

    if (showTooltip) {
      return (
        <Tooltip key={section.id} content={section.label} side="right" delay={120}>
          {button}
        </Tooltip>
      );
    }

    return <div key={section.id}>{button}</div>;
  };

  const navWidth = collapsed ? "var(--nav-rail-width)" : flyoutOpen ? "var(--nav-open-width)" : "var(--nav-rail-width)";

  return (
    <aside
      aria-label="Primary navigation"
      className="relative sticky top-0 z-40 hidden h-[100dvh] lg:flex flex-shrink-0 transition-[width] duration-200 ease-out motion-reduce:transition-none"
      style={{
        ...navVars,
        width: navWidth
      }}
    >
      <div
        ref={railRef}
        onMouseEnter={clearCloseTimer}
        onMouseLeave={scheduleClose}
        className="flex h-full flex-col items-center border-r border-border/50 bg-card/70 px-2 py-4 backdrop-blur-xl"
        style={{ width: "var(--nav-rail-width)" }}
      >
        <div className="mb-4 w-full">
          <Logo collapsed />
        </div>

        <nav className="flex w-full flex-1 flex-col gap-1.5 overflow-y-auto qb-scroll pb-3">
          {visibleSections.map((section) => renderRailButton(section))}
        </nav>
        {isAdmin && <SystemStatusDot />}
      </div>

      {flyoutVisible && (
        <div
          className="pointer-events-none absolute top-0 h-[100dvh] flex-1"
          style={{ left: "var(--nav-rail-width)", width: "var(--nav-flyout-width)" }}
        >
          <div
            ref={flyoutRef}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
            onFocusCapture={clearCloseTimer}
            onBlurCapture={scheduleClose}
            className={cn(
              "absolute left-0 top-0 flex h-full flex-col border-r border-border/60 bg-card/90 shadow-2xl backdrop-blur-xl transition-[opacity,transform] duration-200",
              prefersReducedMotion ? "transition-none" : "",
              flyoutOpen ? "translate-x-0 opacity-100 pointer-events-auto" : "-translate-x-4 opacity-0 pointer-events-none"
            )}
            style={{ width: "var(--nav-flyout-width)" }}
            aria-hidden={!flyoutOpen}
          >
            {flyoutOpen && currentSection && (
              <>
                <div className="flex h-[var(--topbar-height)] items-center justify-between border-b border-border/60 px-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/70 text-foreground">
                      <Icon
                        icon={currentSection.icon ?? "lucide:folder"}
                        className="h-5 w-5"
                      />
                    </span>
                    <div className="space-y-0.5">
                      <div className="text-sm font-semibold leading-tight">
                        {currentSection.label}
                      </div>
                      <div className="text-xs text-muted-foreground">Quick navigation</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {/* Category reorder toggle (Software section only, when groups exist) */}
                    {currentSection.id === "software" && currentSection.groups && currentSection.groups.length > 1 && (
                      <Tooltip content={reorderMode ? "Exit reorder" : "Reorder categories"} side="bottom" delay={200}>
                        <button
                          type="button"
                          onClick={() => setReorderMode((prev) => !prev)}
                          className={cn(
                            "flex h-9 w-9 items-center justify-center rounded-full border border-border/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                            reorderMode
                              ? "bg-primary/10 text-primary border-primary/30"
                              : "bg-muted/70 text-muted-foreground hover:text-foreground"
                          )}
                          aria-label={reorderMode ? "Exit reorder mode" : "Reorder categories"}
                          aria-pressed={reorderMode}
                        >
                          <Icon icon="lucide:arrow-up-down" className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    )}
                    <button
                      type="button"
                      onClick={handlePinToggle}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-border/60 bg-muted/70 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                      aria-label={pinnedSectionId === currentSection.id ? "Unpin menu" : "Pin menu"}
                    >
                      <Icon
                        icon={
                          pinnedSectionId === currentSection.id
                            ? "lucide:lock"
                            : "lucide:pin"
                        }
                        className="h-4 w-4"
                      />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto qb-scroll px-3 py-3">
                  {/* ── Reorder mode: draggable category list ──────────────── */}
                  {reorderMode && currentSection.id === "software" && currentSection.groups && currentSection.groups.length > 1 ? (
                    <SidebarCategoryReorder
                      groups={currentSection.groups}
                      categoryIds={currentCategoryIds}
                      userCategoryOrder={userPrefs.softwareQuickNavCategoryOrder}
                      prefsSaving={prefsSaving}
                      updatePrefs={updatePrefs}
                      onExitReorder={() => setReorderMode(false)}
                    />
                  ) : (
                    <>
                      {/* Empty state for the software section when no apps are installed */}
                      {currentSection.id === "software" && currentSection.items.length === 0 && !currentSection.groups && (
                        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
                          <Icon icon="lucide:package-open" className="h-8 w-8 text-muted-foreground/50" />
                          <p className="text-sm text-muted-foreground">No applications installed yet</p>
                        </div>
                      )}
                      {/* Render flat items first (if section has items without groups, or has both) */}
                      {currentSection.items.length > 0 && !currentSection.groups && (
                        <ul className="space-y-1">
                          {currentSection.items.map((item) => (
                            <li key={item.id}>{renderFlyoutLink(item)}</li>
                          ))}
                        </ul>
                      )}
                      {/* Render flat items + groups interleaved in a single list */}
                      {currentSection.groups && (() => {
                        // Collect all item IDs that belong to a group (recursively) so we only render
                        // them inside their collapsible group, not as top-level flat items.
                        const collectGroupItemIds = (groups: NavGroup[]): string[] =>
                          groups.flatMap((g) => [...g.items.map((i) => i.id), ...(g.groups ? collectGroupItemIds(g.groups) : [])]);
                        const groupItemIds = new Set(collectGroupItemIds(currentSection.groups));
                        const flatItems = currentSection.items.filter((item) => !groupItemIds.has(item.id));

                        // Groups with a navigable path should only render as expandable
                        // groups when the user is currently on that path (or a nested child's path).
                        // Otherwise they collapse into a simple flat link so the flyout stays clean.
                        const isOnGroupOrChildPath = (group: NavGroup): boolean => {
                          if (group.path && (location.pathname === group.path || location.pathname.startsWith(`${group.path}/`))) return true;
                          return group.groups?.some(isOnGroupOrChildPath) ?? false;
                        };
                        const expandedGroupIds = new Set<string>();
                        const collapsedGroupLinkMap = new Map<string, NavLinkItem>();
                        for (const group of currentSection.groups) {
                          if (group.path && !isOnGroupOrChildPath(group)) {
                            // Render as a flat link when not on the group's page
                            collapsedGroupLinkMap.set(group.id, {
                              id: group.id,
                              label: group.label,
                              path: group.path,
                              icon: group.icon ?? "lucide:folder"
                            });
                          } else {
                            expandedGroupIds.add(group.id);
                          }
                        }

                        // Build a unified entry list: flat items first, then groups
                        // in their declared order — each either as a link or expanded group.
                        type FlyoutEntry =
                          | { kind: "item"; item: NavLinkItem }
                          | { kind: "group"; group: NavGroup };

                        const entries: FlyoutEntry[] = [
                          ...flatItems.map((item): FlyoutEntry => ({ kind: "item", item })),
                          ...currentSection.groups.map((group): FlyoutEntry => {
                            if (expandedGroupIds.has(group.id)) {
                              return { kind: "group", group };
                            }
                            // Collapsed navigable group renders as a flat link
                            return { kind: "item", item: collapsedGroupLinkMap.get(group.id)! };
                          })
                        ];

                        return (
                          <ul className="space-y-1">
                            {entries.map((entry) =>
                              entry.kind === "item" ? (
                                <li key={entry.item.id}>{renderFlyoutLink(entry.item)}</li>
                              ) : (
                                <li key={entry.group.id}>{renderFlyoutGroup(entry.group)}</li>
                              )
                            )}
                          </ul>
                        );
                      })()}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </aside>
  );
};
